import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { faro } from '@grafana/faro-web-sdk';
import { catchError, map, Observable, of } from 'rxjs';
import { MulesoftService } from './mulesoft.service';
import { MulesoftOffering } from '../models/mulesoft-offering';
import {
  buildOfferingIndex,
  mapMulesoftOfferingToComponents,
  mapMulesoftToOfferingSummary,
} from '../mappers/mulesoft-offering.mapper';

/** Folder/path offerings live under — required when fetching one by slug. */
const OFFERINGS_FOLDER = 'offerings';

/** Last-modified timestamp (ms) of a draft offering, for overlay reconciliation. */
function draftTimestamp(offering: MulesoftOffering): number {
  const stamp = offering.updatedAt ?? offering.publishedAt;
  return stamp ? new Date(stamp).getTime() : 0;
}

/**
 * Approval statuses that make an offering part of the public catalogue. The
 * list view filters on these; the detail fetch does not, so anything reading a
 * single offering has to apply them itself.
 */
export const APPROVED_STATUSES = [
  'Approved (Preferred)',
  'Approved',
  'Approved with Conditions',
  'Retiring (Seek Alternative)',
  'Retiring',
];

/**
 * Offering reads, backed by the Mulesoft Process API.
 *
 * Single-offering detail (focus form edit-load, public detail page) is fetched
 * directly by id/uuid/slug via `MulesoftService.getOffering`, so a single
 * offering never has to be dug out of the full list. The list endpoint still
 * backs summaries, filters and — for the detail page — relation resolution
 * (`similarOfferings`/`successor`/…) against the loaded set.
 */
@Injectable({ providedIn: 'root' })
export class OfferingsService {
  private readonly _mulesoft = inject(MulesoftService);

  isLoadingOfferings = signal(true);
  isLoadingDraftOfferings = signal(true);

  /**
   * Raw published offerings (approval-filtered), embedded knowledge included.
   * Fetched once, lazily on first read. In-session updates are layered on via
   * the optimistic overlay rather than re-fetching.
   */
  private readonly _publishedOfferings = toSignal(
    this._mulesoft.getOfferings('published', true).pipe(
      map((offerings) =>
        offerings.filter((o) =>
          APPROVED_STATUSES.includes(o.approvalStatus ?? ''),
        ),
      ),
      map((offerings) => {
        this.isLoadingOfferings.set(false);
        return offerings;
      }),
      catchError((error) => {
        faro?.api?.pushError?.(
          error instanceof Error ? error : new Error(String(error)),
        );
        this.isLoadingOfferings.set(false);
        return of<MulesoftOffering[]>([]);
      }),
    ),
    { initialValue: [] as MulesoftOffering[] },
  );

  /**
   * Raw draft offerings (unfiltered), embedded knowledge included. Fetched once,
   * lazily on first read; kept current via the optimistic overlay (see
   * `upsertDraftOptimistic`) instead of re-fetching.
   */
  private readonly _draftOfferings = toSignal(
    this._mulesoft.getOfferings('draft', true).pipe(
      map((offerings) => {
        this.isLoadingDraftOfferings.set(false);
        return offerings;
      }),
      catchError((error) => {
        faro?.api?.pushError?.(
          error instanceof Error ? error : new Error(String(error)),
        );
        this.isLoadingDraftOfferings.set(false);
        return of<MulesoftOffering[]>([]);
      }),
    ),
    { initialValue: [] as MulesoftOffering[] },
  );

  /**
   * Just-saved draft offerings layered on top of the once-fetched list — the
   * sole update path now that the list is not re-fetched. Keeps a created/edited
   * offering visible in "my offerings" immediately after save.
   */
  private readonly _optimisticDrafts = signal<MulesoftOffering[]>([]);

  /** Fetched draft set with just-saved offerings layered on top. */
  private readonly _mergedDraftOfferings = computed(() => {
    const fetched = this._draftOfferings();
    const pending = this._optimisticDrafts();
    if (pending.length === 0) {
      return fetched;
    }
    const byUuid = new Map(fetched.map((o) => [o.uuid, o]));
    for (const saved of pending) {
      const current = byUuid.get(saved.uuid);
      // Overlay the saved copy over the once-fetched entry (or add it if new).
      if (!current || draftTimestamp(current) <= draftTimestamp(saved)) {
        byUuid.set(saved.uuid, saved);
      }
    }
    return Array.from(byUuid.values());
  });

  /** Approved published offering summaries (public site). */
  public offerings = computed(() =>
    this._publishedOfferings().map(mapMulesoftToOfferingSummary),
  );

  /** All draft offering summaries (focus portal). */
  public draftOfferings = computed(() =>
    this._mergedDraftOfferings().map(mapMulesoftToOfferingSummary),
  );

  /**
   * Optimistically add/replace a just-saved offering in the draft set so it
   * stays visible in "my offerings" even while the backend read cache catches
   * up. Safe to call with the write response or the loaded read model.
   */
  public upsertDraftOptimistic(offering: MulesoftOffering | undefined): void {
    if (!offering?.uuid) {
      return;
    }
    this._optimisticDrafts.update((list) => [
      ...list.filter((o) => o.uuid !== offering.uuid),
      offering,
    ]);
  }

  /** Find the draft offering linked to a given knowledge UUID. */
  public findOfferingDraftByKnowledge(knowledgeUuid: string) {
    return this.draftOfferings().find((offering) => {
      const knowledge = offering.knowledge ?? '';
      return knowledge.includes(knowledgeUuid);
    });
  }

  /** Fetch a single draft offering (raw) by uuid — focus form edit-load. */
  public fetchDraftOffering(
    uuid: string,
  ): Observable<MulesoftOffering | undefined> {
    return this._mulesoft.getOffering(uuid, 'draft', { uuid: true }).pipe(
      catchError((error) => {
        faro?.api?.pushError?.(
          error instanceof Error ? error : new Error(String(error)),
        );
        return of<MulesoftOffering | undefined>(undefined);
      }),
    );
  }

  /** Fetch a single published offering (raw) by slug — public detail page. */
  public fetchPublishedOffering(
    slug: string,
  ): Observable<MulesoftOffering | undefined> {
    return this._mulesoft
      .getOffering(slug, 'published', { folder: OFFERINGS_FOLDER })
      .pipe(
        catchError((error) => {
          faro?.api?.pushError?.(
            error instanceof Error ? error : new Error(String(error)),
          );
          return of<MulesoftOffering | undefined>(undefined);
        }),
      );
  }

  /** Resolve a draft offering (raw) by UUID — used to link knowledge to its offering. */
  public getDraftOfferingByUuid(uuid: string): MulesoftOffering | undefined {
    return this._mergedDraftOfferings().find((o) => o.uuid === uuid);
  }

  /** Resolve a draft offering (raw) by id. */
  public getDraftOfferingById(id: string): MulesoftOffering | undefined {
    return this._mergedDraftOfferings().find(
      (o) => String(o.id) === String(id),
    );
  }

  /** Resolve the draft offering carrying a given knowledge UUID. */
  public getDraftOfferingByKnowledgeUuid(
    knowledgeUuid: string,
  ): MulesoftOffering | undefined {
    return this._mergedDraftOfferings().find(
      (o) => (o.knowledge ?? '') === knowledgeUuid,
    );
  }

  /** Resolve a draft knowledge object by its UUID. */
  public getDraftKnowledgeByUuid(knowledgeUuid: string) {
    return this._mergedDraftOfferings().find(
      (o) => o.knowledgeContent?.uuid === knowledgeUuid,
    )?.knowledgeContent;
  }

  /**
   * Map a (directly-fetched) published offering into the detail page's component
   * array, resolving relation refs against the loaded published set.
   */
  public buildOfferingComponents(offering: MulesoftOffering): any[] {
    const index = buildOfferingIndex(this._publishedOfferings());
    return mapMulesoftOfferingToComponents(offering, index);
  }

  public offeringsByCategories = computed(() => {
    const offerings = this.offerings();
    const categoryMap = new Map<string, any[]>();

    for (const offering of offerings) {
      const categories = offering.categories ?? [];

      if (categories.length === 0) {
        const key = 'Others';
        if (!categoryMap.has(key)) {
          categoryMap.set(key, []);
        }
        categoryMap.get(key)?.push(offering);
      } else {
        for (const category of categories) {
          if (!categoryMap.has(category) && category.length > 0) {
            categoryMap.set(category, []);
          }
          categoryMap.get(category)?.push(offering);
        }
      }
    }

    return Array.from(categoryMap.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  });

  public offeringCategories = computed(() => {
    const categorySet = new Set<string>();
    for (const offering of this.offerings()) {
      const categories = offering.categories ?? [];
      if (categories.length === 0) {
        categorySet.add('Other');
      } else {
        for (const category of categories) {
          if (category.length > 0) {
            categorySet.add(category);
          }
        }
      }
    }
    return Array.from(categorySet);
  });

  public offeringStatuses = computed(() => {
    const statusesSet = new Set<string>();
    for (const offering of this.offerings()) {
      if (offering.approvalStatus) {
        statusesSet.add(offering.approvalStatus);
      }
    }
    return Array.from(statusesSet);
  });

  public offeringClassifications = computed(() => {
    const classificationsSet = new Set<string>();
    for (const offering of this.offerings()) {
      if (offering.classificationLevel) {
        classificationsSet.add(offering.classificationLevel);
      }
    }
    return Array.from(classificationsSet);
  });

  public offeringTypes = computed(() => {
    const typesSet = new Set<string>();
    for (const offering of this.offerings()) {
      if (offering.offeringType) {
        typesSet.add(offering.offeringType);
      }
    }
    return Array.from(typesSet);
  });
}
